import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PivotMode, SearchMode, FilterMode } from '../components/transactions-v2';

export type FlowFilter = 'all' | 'income' | 'expense';

export interface PivotState {
  // Context
  startDate: string;
  endDate: string;
  accountIds: string[];

  // Visualization
  viewMode: 'transactions' | 'pivot' | 'flow' | 'balance';

  // Pivot Configuration (global orientation)
  pivotMode: PivotMode;
  pivotOrientation: 'vertical' | 'horizontal';

  // Filters
  searchQuery: string;
  searchMode: SearchMode;
  filterMode: FilterMode;
  flowFilter: FlowFilter;

  // UI
  page: number;
  pageSize: number;
  isAiOpen: boolean;
  isExportOpen: boolean;
}

export interface PivotActions {
  // Context actions
  setDateRange(start: string, end: string): void;
  goToPreviousPeriod(): void;
  goToNextPeriod(): void;
  setAccountIds(ids: string[]): void;

  // View actions
  setViewMode(mode: 'transactions' | 'pivot' | 'flow' | 'balance'): void;

  // Pivot actions
  setPivotMode(mode: PivotMode): void;
  setPivotOrientation(o: 'vertical' | 'horizontal'): void;

  // Filter actions
  setSearchQuery(q: string): void;
  setSearchMode(m: SearchMode): void;
  setFilterMode(f: FilterMode): void;
  setFlowFilter(f: FlowFilter): void;

  // UI actions
  setPage(p: number): void;
  setPageSize(p: number): void;
  toggleAiPanel(): void;
  toggleExportMenu(): void;
}

const VIEW_MODES: PivotState['viewMode'][] = ['transactions', 'pivot', 'flow', 'balance'];
const SEARCH_MODES: SearchMode[] = ['merchant', 'category'];
const FILTER_MODES: FilterMode[] = ['none', 'checks', 'zelle', 'transfer'];
const FLOW_FILTERS: FlowFilter[] = ['all', 'income', 'expense'];
const ORIENTATIONS: PivotState['pivotOrientation'][] = ['vertical', 'horizontal'];

function readEnum<T extends string>(
  params: URLSearchParams,
  key: string,
  allowed: readonly T[],
  fallback: T,
): T {
  const raw = params.get(key);
  return (allowed as readonly string[]).includes(raw ?? '') ? (raw as T) : fallback;
}

/**
 * Custom hook for managing pivot state across all 5 zones.
 * Centralizes state management and provides typed actions.
 *
 * Filter/view state (everything except page/pageSize and the transient
 * isAiOpen/isExportOpen menu flags) is mirrored to the URL query string, so
 * refreshing the page or using the browser's back/forward buttons doesn't
 * silently discard the user's date range, search, or view choice — see
 * docs/plans/TRANSACTIONS_PAGINATION_PLAN.md, R1-3. page/pageSize are NOT
 * synced here deliberately: TransactionsExplorer manages its own separate,
 * per-data-source page state (snapshotPage/originalPage) rather than
 * reading these fields — syncing them would silently do nothing, which is
 * worse than not syncing them at all. Shared by both /transactions
 * (TenantTransactionsV2Page) and /snapshots (SnapshotPage), so this one
 * change covers both screens' filter persistence.
 */
export function usePivotState(): [PivotState, PivotActions] {
  const [searchParams, setSearchParams] = useSearchParams();

  const now = new Date();
  const defaultStart = new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .slice(0, 10);
  const defaultEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    .toISOString()
    .slice(0, 10);

  const [state, setState] = useState<PivotState>(() => ({
    startDate: searchParams.get('startDate') || defaultStart,
    endDate: searchParams.get('endDate') || defaultEnd,
    accountIds: searchParams.get('accountIds')?.split(',').filter(Boolean) ?? [],
    viewMode: readEnum(searchParams, 'viewMode', VIEW_MODES, 'transactions'),
    pivotMode: readEnum(
      searchParams,
      'pivotMode',
      [
        'vendor_month', 'vendor_year', 'merchant_month', 'merchant_year',
        'category_month', 'category_year', 'subcategory_month', 'subcategory_year',
        'total_month', 'total_year',
      ] as const,
      'vendor_month',
    ),
    pivotOrientation: readEnum(searchParams, 'pivotOrientation', ORIENTATIONS, 'vertical'),
    searchQuery: searchParams.get('q') || '',
    searchMode: readEnum(searchParams, 'searchMode', SEARCH_MODES, 'merchant'),
    filterMode: readEnum(searchParams, 'filterMode', FILTER_MODES, 'none'),
    flowFilter: readEnum(searchParams, 'flowFilter', FLOW_FILTERS, 'all'),
    page: 1,
    pageSize: 20,
    isAiOpen: false,
    isExportOpen: false,
  }));

  // Context actions
  const setDateRange = useCallback((start: string, end: string) => {
    setState((s) => ({ ...s, startDate: start, endDate: end, page: 1 }));
  }, []);

  const goToPreviousPeriod = useCallback(() => {
    setState((s) => {
      const start = new Date(s.startDate + 'T00:00:00');
      const end = new Date(s.endDate + 'T00:00:00');
      const diff = end.getTime() - start.getTime();
      const ps = new Date(start.getTime() - diff - 86400000);
      const pe = new Date(start.getTime() - 86400000);
      return {
        ...s,
        startDate: ps.toISOString().slice(0, 10),
        endDate: pe.toISOString().slice(0, 10),
        page: 1,
      };
    });
  }, []);

  const goToNextPeriod = useCallback(() => {
    setState((s) => {
      const start = new Date(s.startDate + 'T00:00:00');
      const end = new Date(s.endDate + 'T00:00:00');
      const diff = end.getTime() - start.getTime();
      const ps = new Date(end.getTime() + 86400000);
      const pe = new Date(end.getTime() + diff + 86400000);
      return {
        ...s,
        startDate: ps.toISOString().slice(0, 10),
        endDate: pe.toISOString().slice(0, 10),
        page: 1,
      };
    });
  }, []);

  const setAccountIds = useCallback((ids: string[]) => {
    setState((s) => ({ ...s, accountIds: ids, page: 1 }));
  }, []);

  // View actions
  const setViewMode = useCallback(
    (mode: 'transactions' | 'pivot' | 'flow' | 'balance') => {
      setState((s) => ({ ...s, viewMode: mode }));
    },
    []
  );

  // Pivot actions
  const setPivotMode = useCallback((mode: PivotMode) => {
    setState((s) => ({ ...s, pivotMode: mode }));
  }, []);

  const setPivotOrientation = useCallback((o: 'vertical' | 'horizontal') => {
    setState((s) => ({ ...s, pivotOrientation: o }));
  }, []);

  // Filter actions
  const setSearchQuery = useCallback((q: string) => {
    setState((s) => ({ ...s, searchQuery: q, page: 1 }));
  }, []);

  const setSearchMode = useCallback((m: SearchMode) => {
    setState((s) => ({ ...s, searchMode: m, page: 1 }));
  }, []);

  const setFilterMode = useCallback((f: FilterMode) => {
    setState((s) => ({ ...s, filterMode: f, page: 1 }));
  }, []);

  const setFlowFilter = useCallback((f: FlowFilter) => {
    setState((s) => ({ ...s, flowFilter: f, page: 1 }));
  }, []);

  // UI actions
  const setPage = useCallback((p: number) => {
    setState((s) => ({ ...s, page: p }));
  }, []);

  const setPageSize = useCallback((p: number) => {
    setState((s) => ({ ...s, pageSize: p, page: 1 }));
  }, []);

  const toggleAiPanel = useCallback(() => {
    setState((s) => ({ ...s, isAiOpen: !s.isAiOpen }));
  }, []);

  const toggleExportMenu = useCallback(() => {
    setState((s) => ({ ...s, isExportOpen: !s.isExportOpen }));
  }, []);

  const actions: PivotActions = {
    setDateRange,
    goToPreviousPeriod,
    goToNextPeriod,
    setAccountIds,
    setViewMode,
    setPivotMode,
    setPivotOrientation,
    setSearchQuery,
    setSearchMode,
    setFilterMode,
    setFlowFilter,
    setPage,
    setPageSize,
    toggleAiPanel,
    toggleExportMenu,
  };

  // Mirror filter/view state to the URL (replace, not push — every filter
  // tweak or keystroke would otherwise spam browser history). Deliberately
  // excludes page/pageSize (see the hook's docstring) and isAiOpen/
  // isExportOpen (transient menu state, not meaningful to share via URL).
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('startDate', state.startDate);
    params.set('endDate', state.endDate);
    if (state.accountIds.length > 0) params.set('accountIds', state.accountIds.join(','));
    if (state.viewMode !== 'transactions') params.set('viewMode', state.viewMode);
    if (state.pivotMode !== 'vendor_month') params.set('pivotMode', state.pivotMode);
    if (state.pivotOrientation !== 'vertical') params.set('pivotOrientation', state.pivotOrientation);
    if (state.searchQuery) params.set('q', state.searchQuery);
    if (state.searchMode !== 'merchant') params.set('searchMode', state.searchMode);
    if (state.filterMode !== 'none') params.set('filterMode', state.filterMode);
    if (state.flowFilter !== 'all') params.set('flowFilter', state.flowFilter);
    setSearchParams(params, { replace: true });
    // setSearchParams is stable per React Router; omitting it would be
    // harmless either way, but including the individual state fields (not
    // `state` itself) avoids re-running this on page/pageSize/menu changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.startDate,
    state.endDate,
    state.accountIds,
    state.viewMode,
    state.pivotMode,
    state.pivotOrientation,
    state.searchQuery,
    state.searchMode,
    state.filterMode,
    state.flowFilter,
  ]);

  return [state, actions];
}
